import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts';
import { ErrorI, GetServicesI, OnDemandHoursI, PostOnDemandHoursI } from '@/types/cyclone/requests';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components';
import { ScheduleI } from '@/containers/ActionTeam/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { scrollBarTW } from '@/components/App';
import { useClient, useWindowResize } from '@/hooks';
import { InputTimePicker } from '@/components/Input';
import { ServiceStatusE, VendorI, VendorOnDemandHoursI } from '@/types/cyclone/models';
import { useMutation, useQuery } from 'react-query';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { EmptyObject } from '../../types';
import { Clock, Plus } from 'lucide-react';

type Props = {
  show: boolean;
  setShow: (show: boolean) => void;
  refetch: () => void;
  isVenue: boolean;
  onDemandHours?: VendorOnDemandHoursI[];
  date: Date;
  onWorkingHours: () => void;
  vendorId?: number;
  onDemandHourId?: string;
};

const DEFAULT_SCHEDULE = { start_time: '09:00', end_time: '18:00' };

const areWorkingHoursEqual = (hours1: any[], hours2: any[]): boolean => {
  if (hours1.length !== hours2.length) return false;

  return hours1.every((h1, index) => {
    const h2 = hours2[index];
    return h1.start_time === h2.start_time && h1.end_time === h2.end_time;
  });
};

export const OnDemandHoursModal: React.FC<Props> = React.memo(
  ({
    show,
    setShow,
    isVenue,
    date: _date,
    onDemandHours: _onDemandHours,
    refetch,
    onWorkingHours,
    vendorId,
    onDemandHourId
  }) => {
    const { session } = useAuth();
    const { client } = useClient();
    const { isMobileSize } = useWindowResize();

    const params = useMemo(() => new URLSearchParams(window.location.search), []);

    const [step, setStep] = useState(1);
    const [schedulesSelected, setSchedulesSelected] = useState<ScheduleI[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<VendorI | undefined>(undefined);
    const [selectedServicesId, setSelectedServicesId] = useState<number[]>([]);
    const [allSelected, setAllSelected] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const { onDemandHours, isEdit, date, disableDateRange, formattedDate } = useMemo(() => {
      const parsedOnDemandHourId = onDemandHourId ? Number(onDemandHourId) : undefined;
      const filteredOnDemandHours = parsedOnDemandHourId
        ? _onDemandHours?.filter((odh) => odh.id === parsedOnDemandHourId)
        : [];
      const isEdit = !!(filteredOnDemandHours && filteredOnDemandHours.length > 0);
      const date = isEdit ? filteredOnDemandHours[0].start_at : _date;
      const todayOnDemandHours = _onDemandHours?.filter(
        (odh) => odh.start_at === dayjs(_date).format('YYYY-MM-DD')
      );
      const disableDateRange = !!(!isEdit && todayOnDemandHours && todayOnDemandHours?.length > 0);
      const formattedDate = dayjs(date).format('dddd, DD MMMM YYYY');

      return {
        onDemandHours: filteredOnDemandHours,
        isEdit,
        date,
        disableDateRange,
        formattedDate
      };
    }, [_onDemandHours, onDemandHourId, _date]);

    const { isLoading: isLoadingStaff } = useQuery(
      ['venues', 'staffs'],
      async () => await client<VendorI[]>(`venues/staff`, 'GET', { isAuthRequired: true }),
      {
        retry: false,
        refetchOnWindowFocus: false,
        enabled: isVenue && show,
        onSuccess: (data) => {
          if (data.length > 0 && !selectedVendor) {
            if (vendorId) {
              setSelectedVendor(data.find((vendor) => vendor.id === +vendorId));
            } else {
              const urlVendorId = params.get('vendor');
              setSelectedVendor(data.find((vendor) => urlVendorId && vendor.id === +urlVendorId) || data[0]);
            }
          }
        }
      }
    );

    const { data: servicesData } = useQuery(
      ['services'],
      async () => await client<GetServicesI>(`me/vendor/services`, 'GET', { isAuthRequired: true }),
      {
        retry: false,
        refetchOnWindowFocus: false,
        enabled: show
      }
    );

    const services = useMemo(() => servicesData || [], [servicesData]);

    const filteredServices = useMemo(() => {
      let filtered = services;

      if (session?.vendor?.id === 2529) {
        filtered = services.filter((service) => service.is_on_demand);
      } else {
        filtered = services
          .filter((service) => service.is_on_demand)
          .filter((s) => s.status === ServiceStatusE.ACTIVE);
      }

      if (selectedVendor && selectedVendor.id) {
        return filtered.filter((s) => {
          if (s.multi_vendors.length > 0) {
            return s.multi_vendors.some((v) => v.vendor_id === +selectedVendor.id);
          } else {
            return s.vendor_id === +selectedVendor.id;
          }
        });
      }

      return filtered;
    }, [services, selectedVendor, session?.vendor?.id]);

    const { isSaveDisabled, isAddScheduleDisabled } = useMemo(() => {
      const hasEmptyFields = schedulesSelected.some((schedule) => !schedule.start_time || !schedule.end_time);
      return {
        isSaveDisabled: hasEmptyFields || schedulesSelected.length === 0,
        isAddScheduleDisabled: hasEmptyFields
      };
    }, [schedulesSelected]);

    const handleSelectAll = useCallback(
      (isSelected: boolean) => {
        if (isSelected) {
          setSelectedServicesId(filteredServices.map((service) => service.id));
          setAllSelected(true);
        } else {
          setSelectedServicesId([]);
          setAllSelected(false);
        }
      },
      [filteredServices]
    );

    const handleServiceChange = useCallback(
      (serviceId: number) => {
        setSelectedServicesId((prev) => {
          const isSelected = prev.includes(serviceId);
          const newSelected = isSelected ? prev.filter((id) => id !== serviceId) : [...prev, serviceId];

          const allServicesSelected = filteredServices.every((service) => newSelected.includes(service.id));

          setAllSelected(allServicesSelected);
          return newSelected;
        });
      },
      [filteredServices]
    );

    const handleSelectChange = useCallback((value: string, index: number, key: keyof ScheduleI) => {
      setSchedulesSelected((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [key]: value };

        if (key === 'end_time' && index < prev.length - 1) {
          updated[index + 1] = { ...updated[index + 1], start_time: value };
        }

        return updated;
      });
    }, []);

    const deleteSchedule = useCallback((index: number) => {
      setSchedulesSelected((prev) => {
        const updated = prev.filter((_, i) => i !== index);

        if (index < prev.length - 1 && index > 0) {
          updated[index] = {
            ...updated[index],
            start_time: prev[index - 1].end_time
          };
        }

        return updated;
      });
    }, []);

    const addNewSchedule = useCallback(() => {
      setSchedulesSelected((prev) => {
        if (prev.length === 0) {
          return [DEFAULT_SCHEDULE];
        }

        const lastSchedule = prev[prev.length - 1];
        return [
          ...prev,
          {
            start_time: lastSchedule.end_time,
            end_time: ''
          }
        ];
      });
    }, []);

    const validateWorkingHours = useCallback((workingHours: OnDemandHoursI['working_hours']): boolean => {
      const isTimeBefore = (time1: string, time2: string): boolean => {
        const [hours1, minutes1] = time1.split(':').map(Number);
        const [hours2, minutes2] = time2.split(':').map(Number);
        return hours1 < hours2 || (hours1 === hours2 && minutes1 < minutes2);
      };

      const sortedHours = [...workingHours].sort((a, b) =>
        isTimeBefore(a.start_time, b.start_time) ? -1 : 1
      );

      for (let i = 0; i < sortedHours.length; i++) {
        const current = sortedHours[i];

        if (isTimeBefore(current.end_time, current.start_time) || current.end_time === current.start_time) {
          toast.error(
            `El horario de fin debe ser después del horario de inicio en el rango ${current.start_time} - ${current.end_time}.`
          );
          return false;
        }

        if (i < sortedHours.length - 1) {
          const next = sortedHours[i + 1];
          if (!isTimeBefore(current.end_time, next.start_time)) {
            toast.error(
              `Los rangos de horarios ${current.start_time} - ${current.end_time} y ${next.start_time} - ${next.end_time} se superponen.`
            );
            return false;
          }
        }
      }

      return true;
    }, []);

    const mutation = useMutation<EmptyObject, ErrorI, PostOnDemandHoursI>(
      (data) =>
        client(`me/vendor/on_demand_hours`, 'POST', {
          isAuthRequired: true,
          data
        }),
      {
        onMutate: () => setIsLoading(true),
        onSuccess: () => {
          toast.success('Horarios guardados exitosamente.');
          refetch();
          setShow(false);
        },
        onError: (e) => {
          if (e.code && e.code === 'ODH-001') {
            toast.error('Los horarios ingresados colisionan con otros horarios laborales recurrentes.');
          } else {
            toast.error('Algo anda mal. Por favor, contactar a soporte.');
          }
        },
        onSettled: () => setIsLoading(false)
      }
    );

    const mutationEdit = useMutation<EmptyObject, ErrorI, PostOnDemandHoursI>(
      (data) =>
        client(`me/vendor/on_demand_hours/${onDemandHourId}`, 'PUT', {
          isAuthRequired: true,
          data
        }),
      {
        onMutate: () => setIsLoading(true),
        onSuccess: () => {
          toast.success('Horarios guardados exitosamente.');
          refetch();
          setShow(false);
        },
        onError: (e) => {
          if (e.code && e.code === 'ODH-001') {
            toast.error('Los horarios ingresados colisionan con otros horarios laborales recurrentes.');
          } else {
            toast.error('Algo anda mal. Por favor, contactar a soporte.');
          }
        },
        onSettled: () => setIsLoading(false)
      }
    );

    const handleSave = useCallback(() => {
      const serviceIds = allSelected ? filteredServices.map((service) => service.id) : selectedServicesId;

      if (serviceIds.length === 0) {
        toast.error('Debes seleccionar al menos un servicio.');
        return;
      }

      const onDemandHoursData: OnDemandHoursI = {
        service_ids: serviceIds,
        start_at: dayjs(date).format('YYYY-MM-DD'),
        working_hours: schedulesSelected.map((schedule) => ({
          start_time: schedule.start_time,
          end_time: schedule.end_time
        }))
      };

      // Validar antes de enviar
      if (!validateWorkingHours(onDemandHoursData.working_hours)) return;

      const data: PostOnDemandHoursI = {
        on_demand_hours: onDemandHoursData,
        vendor_id: isVenue ? selectedVendor!.id.toString() : session?.vendor?.id.toString()
      };

      if (isEdit) {
        mutationEdit.mutate(data);
      } else {
        mutation.mutate(data);
      }
    }, [
      allSelected,
      filteredServices,
      selectedServicesId,
      date,
      schedulesSelected,
      validateWorkingHours,
      isVenue,
      selectedVendor,
      session,
      isEdit,
      mutation,
      mutationEdit
    ]);

    const handleContinue = useCallback(() => {
      const workingHours = schedulesSelected.map((schedule) => ({
        start_time: schedule.start_time,
        end_time: schedule.end_time
      }));

      if (!validateWorkingHours(workingHours)) return;
      setStep(2);
    }, [schedulesSelected, validateWorkingHours]);

    useEffect(() => {
      if (show) {
        if (isEdit && onDemandHours && onDemandHours.length > 0) {
          const schedules = onDemandHours[0].working_hours.map((schedule) => ({
            start_time: schedule.start_time,
            end_time: schedule.end_time
          }));
          setSchedulesSelected(schedules);

          const selectedServiceIds: number[] = [];

          if (_onDemandHours && _onDemandHours.length > 0) {
            const targetDate = onDemandHours[0].start_at;
            const targetWorkingHours = onDemandHours[0].working_hours;

            _onDemandHours.forEach((odh) => {
              if (
                odh.start_at === targetDate &&
                areWorkingHoursEqual(odh.working_hours, targetWorkingHours) &&
                odh.service_id &&
                !selectedServiceIds.includes(odh.service_id)
              ) {
                selectedServiceIds.push(odh.service_id);
              }
            });
          }

          setSelectedServicesId(selectedServiceIds);

          if (filteredServices.length > 0) {
            const allServicesSelected = filteredServices.every((service) =>
              selectedServiceIds.includes(service.id)
            );
            setAllSelected(allServicesSelected);
          } else {
            setAllSelected(selectedServiceIds.length === 0);
          }
        } else {
          if (!isEdit) {
            setSchedulesSelected([DEFAULT_SCHEDULE]);
          }

          setSelectedServicesId([]);
          setAllSelected(true);
        }

        setStep(1);
      }
    }, [show, isEdit, onDemandHours, _onDemandHours, filteredServices]);

    const Step1Content = useMemo(() => {
      const isEditMessage = isEdit ? 'Edita' : 'Selecciona una fecha y configura';
      const subtitle = isVenue ? (
        isLoadingStaff || !selectedVendor ? (
          <div className="flex h-[4ch] w-full bg-gray-100 animate-pulse" />
        ) : (
          `${isEditMessage} los horarios disponibles para ${selectedVendor?.user.first_name} ${selectedVendor?.user.last_name} el ${formattedDate}`
        )
      ) : (
        `${isEditMessage} los horarios disponibles para el ${formattedDate}`
      );

      return (
        <div className="py-4">
          <p className="text-sm font-book text-gray-600 mb-4">{subtitle}</p>

          <div className="flex items-center gap-2 mb-3 font-medium">
            <Clock size="16" />
            <span>{disableDateRange ? 'Horario' : 'Rango de horarios'} </span>
          </div>

          <div className={`max-h-[240px] md:max-h-[185px] overflow-y-auto ${scrollBarTW} mb-3`}>
            {schedulesSelected.map((schedule, i) => {
              const previousSchedule = schedulesSelected[i - 1];
              const nextSchedule = schedulesSelected[i + 1];

              const minTime = previousSchedule ? previousSchedule.end_time : undefined;
              const maxTime = nextSchedule ? nextSchedule.start_time : undefined;

              return (
                <div key={i} className="flex items-center justify-between gap-2 mb-3 group">
                  <div className="flex items-center gap-2 w-[calc(100%-40px)]">
                    <div className="w-1/2">
                      <InputTimePicker
                        initialValue={schedule.start_time}
                        onTimeChange={(time) => handleSelectChange(time, i, 'start_time')}
                        minTime={minTime}
                        maxTime={schedule.end_time}
                      />
                    </div>
                    <div className="w-1/2">
                      <InputTimePicker
                        initialValue={schedule.end_time}
                        onTimeChange={(time) => handleSelectChange(time, i, 'end_time')}
                        minTime={schedule.start_time}
                        maxTime={maxTime}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => deleteSchedule(i)}
                    disabled={schedulesSelected.length === 1 || isLoading || isEdit}
                    className={classNames(
                      'h-8 w-8 flex items-center justify-center rounded-full transition-all',
                      schedulesSelected.length === 1 || isEdit
                        ? 'opacity-30 cursor-not-allowed'
                        : 'hover:bg-red-50 opacity-60 group-hover:opacity-100'
                    )}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} className="text-red-500" size="sm" />
                  </button>
                </div>
              );
            })}
          </div>

          {!disableDateRange && !isEdit && (
            <button
              onClick={addNewSchedule}
              disabled={isAddScheduleDisabled || isLoading}
              className={classNames(
                'w-full py-2 border border-dashed rounded-md flex items-center justify-center gap-1 transition-all',
                isAddScheduleDisabled || isLoading
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-blue/20 text-blue hover:bg-blue/10 cursor-pointer'
              )}
            >
              <Plus size={16} />
              <span className="font-medium">Agregar nuevo rango</span>
            </button>
          )}

          <p className="text-xs text-gray-500 mt-2">
            {isVenue
              ? 'Sólo se configurarán los horarios para esta fecha. Para horarios habituales, '
              : 'Para configurar horarios habituales, '}
            <button onClick={onWorkingHours} className="text-xs text-blue underline underline-offset-2">
              haz click aquí
            </button>
            .
          </p>
        </div>
      );
    }, [
      isEdit,
      isVenue,
      selectedVendor,
      formattedDate,
      disableDateRange,
      schedulesSelected,
      handleSelectChange,
      deleteSchedule,
      isAddScheduleDisabled,
      isLoading,
      addNewSchedule,
      onWorkingHours,
      isLoadingStaff
    ]);

    const Step2Content = useMemo(() => {
      const configMessage = isVenue ? (
        isLoadingStaff || !selectedVendor ? (
          <div className="flex h-[1ch] w-full bg-gray-100 animate-pulse" />
        ) : (
          `Selecciona los servicios que ofrecerá ${selectedVendor.user.first_name} ${selectedVendor.user.last_name} el día ${formattedDate}.`
        )
      ) : (
        `Selecciona los servicios que ofrecerás el día ${formattedDate}.`
      );

      return (
        <div className="py-4">
          <p className="text-sm font-book text-gray-600 mb-4">{configMessage}</p>

          <div className={`max-h-[240px] overflow-y-auto ${scrollBarTW} mb-3`}>
            <div className="mb-2">
              <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="checkbox checkbox-primary"
                />
                <span className="text-blue font-medium">Seleccionar todos</span>
              </label>
            </div>

            {filteredServices.map((service) => (
              <div key={service.id} className="mb-1">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={selectedServicesId.includes(service.id) || allSelected}
                    onChange={() => handleServiceChange(service.id)}
                    className="checkbox checkbox-primary"
                  />
                  <span>{service.name}</span>
                </label>
              </div>
            ))}

            {filteredServices.length === 0 && (
              <p className="text-gray-500 text-center py-4">No hay servicios disponibles</p>
            )}
          </div>
        </div>
      );
    }, [
      isVenue,
      selectedVendor,
      formattedDate,
      allSelected,
      filteredServices,
      selectedServicesId,
      handleSelectAll,
      handleServiceChange,
      isLoadingStaff
    ]);

    const renderStep = () => {
      switch (step) {
        case 1:
          return Step1Content;
        case 2:
          return Step2Content;
        default:
          return null;
      }
    };

    return (
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-3 border-b border-[#DACCE0]">
            <div className="grid grid-cols-6 w-full">
              <div className="flex self-start">
                {step === 1 ? (
                  <DialogClose />
                ) : (
                  <span className="cursor-pointer text-gray-500 text-sm" onClick={() => setStep(1)}>
                    Atrás
                  </span>
                )}
              </div>
              <DialogTitle className="col-span-4 self-center">
                {step === 1 ? `${isEdit ? 'Editar horarios' : 'Agregar horarios'}` : 'Servicios disponibles'}
              </DialogTitle>
              {step === 2 ? (
                <button onClick={handleSave} className="self-end text-sm font-medium text-[#0072FB]">
                  Guardar
                </button>
              ) : (
                <div />
              )}
            </div>
          </DialogHeader>

          {renderStep()}

          <DialogFooter fixed={isMobileSize}>
            <Button
              rounded
              fullWidth
              onClick={step === 1 ? handleContinue : handleSave}
              disabled={step === 1 ? isSaveDisabled : isLoading}
              loading={isLoading}
            >
              {step === 1 ? 'Continuar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
